import React, { useState } from 'react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import { RadioGroup, RadioGroupItem } from '@/components/ui/radio-group';
import { Alert, AlertDescription } from '@/components/ui/alert';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { AlertCircle, Pencil } from 'lucide-react';
import { getSupportedGeometryVersions, resolveGeometryVersion } from '@/lib/drafting';

const API_BASE = process.env.REACT_APP_BACKEND_URL || '';

const JACKET_BELLY_PROFILE_OPTIONS = [
  { value: 'slim', label: 'Slim' },
  { value: 'normal', label: 'Normal' },
  { value: 'slight_belly', label: 'Slight Belly' },
  { value: 'big_belly', label: 'Big Belly' },
  { value: 'extreme_belly', label: 'Extreme Belly' },
];

const JACKET_POSTURE_OPTIONS = [
  { value: 'normal', label: 'Normal' },
  { value: 'stoop', label: 'Stoop' },
  { value: 'sway', label: 'Sway' },
];

export default function ActionConfirmModal({ 
  open, 
  onOpenChange, 
  pattern, 
  onConfirm,
  token,
  canSavePatternVersion = true,
}) {
  const [action, setAction] = useState('overwrite');
  const [editingPatternNo, setEditingPatternNo] = useState(false);
  const [newPatternNumber, setNewPatternNumber] = useState('');
  const [revisionGeometryVersion, setRevisionGeometryVersion] = useState('');
  const [revisionBellyProfile, setRevisionBellyProfile] = useState('normal');
  const [revisionPosture, setRevisionPosture] = useState('normal');
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(false);

  const garmentType = (pattern?.garment_type || 'trouser').toLowerCase();
  const supportedGeometryVersions = getSupportedGeometryVersions(garmentType);
  const orderedGeometryVersions = [...supportedGeometryVersions];

  // Generate next version number
  React.useEffect(() => {
    if (pattern && !editingPatternNo) {
      const match = pattern.pattern_number.match(/^([A-Z]\d+)-(\d+)$/);
      if (match) {
        const base = match[1];
        const version = parseInt(match[2]);
        setNewPatternNumber(`${base}-${version + 1}`);
      }
    }
  }, [pattern, editingPatternNo]);

  React.useEffect(() => {
    if (!open) return;
    setError('');
    setEditingPatternNo(false);
    const inheritedGeometry = resolveGeometryVersion(garmentType, pattern || {});
    setRevisionGeometryVersion(inheritedGeometry);
    setRevisionBellyProfile(String(pattern?.measurements?.bellyProfile || 'normal').toLowerCase());
    setRevisionPosture(String(pattern?.measurements?.posture || 'normal').toLowerCase());
    if (!canSavePatternVersion) {
      setAction('overwrite');
    }
  }, [open, canSavePatternVersion, garmentType, pattern]);

  const validatePatternExists = async (patternNumber) => {
    try {
      const response = await fetch(`${API_BASE}/api/patterns/exists/${patternNumber}`, {
        headers: { 'Authorization': `Bearer ${token}` }
      });
      const data = await response.json();
      return data.exists;
    } catch (err) {
      console.error('Error checking pattern existence:', err);
      return false;
    }
  };

  const handleProceed = async () => {
    setError('');
    setLoading(true);

    try {
      if (action === 'new_version') {
        // Validate new pattern number doesn't exist
        const exists = await validatePatternExists(newPatternNumber);
        if (exists) {
          setError(`Pattern ${newPatternNumber} already exists. Please use a different version number.`);
          setLoading(false);
          return;
        }
      }

      onConfirm({
        action,
        pattern,
        newPatternNumber: action === 'new_version' ? newPatternNumber : null,
        geometryVersion: action === 'new_version'
          ? (revisionGeometryVersion || resolveGeometryVersion(garmentType, pattern || {}))
          : null,
        bellyProfile: action === 'new_version' && garmentType === 'jacket'
          ? revisionBellyProfile
          : null,
        posture: action === 'new_version' && garmentType === 'jacket'
          ? revisionPosture
          : null,
      });
      
      onOpenChange(false);
    } catch (err) {
      setError(err.message);
    } finally {
      setLoading(false);
    }
  };

  if (!pattern) return null;

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-md">
        <DialogHeader>
          <DialogTitle>{pattern.pattern_number} - {pattern.client_name}</DialogTitle>
          <DialogDescription>
            What would you like to do with this pattern?
          </DialogDescription>
        </DialogHeader>
        
        <div className="space-y-4 py-4">
          {error && (
            <Alert variant="destructive">
              <AlertCircle className="h-4 w-4" />
              <AlertDescription>{error}</AlertDescription>
            </Alert>
          )}

          <RadioGroup value={action} onValueChange={setAction}>
            <div className="flex items-start space-x-3 space-y-0 rounded-md border p-4 hover:bg-muted">
              <RadioGroupItem value="overwrite" id="overwrite" />
              <div className="flex-1">
                <Label htmlFor="overwrite" className="font-semibold cursor-pointer">
                  {canSavePatternVersion ? 'Edit & Overwrite This Version' : 'Open This Version'}
                </Label>
                <p className="text-sm text-muted-foreground mt-1">
                  {canSavePatternVersion
                    ? `${pattern.pattern_number} will be updated`
                    : `${pattern.pattern_number} will open in read-only mode for preview/print`}
                </p>
              </div>
            </div>

            {canSavePatternVersion && (
              <div className="flex items-start space-x-3 space-y-0 rounded-md border p-4 hover:bg-muted">
                <RadioGroupItem value="new_version" id="new_version" />
                <div className="flex-1">
                  <Label htmlFor="new_version" className="font-semibold cursor-pointer">
                    Create New Revision
                  </Label>
                  {action === 'new_version' && (
                    <div className="mt-3 space-y-2">
                      <Label className="text-xs text-muted-foreground">New Pattern Number</Label>
                      <div className="flex items-center gap-2">
                        {editingPatternNo ? (
                          <Input
                            value={newPatternNumber}
                            onChange={(e) => setNewPatternNumber(e.target.value.toUpperCase())}
                            onBlur={() => setEditingPatternNo(false)}
                            autoFocus
                            className="uppercase flex-1"
                          />
                        ) : (
                          <div className="flex-1 px-3 py-2 border rounded-md bg-muted font-mono text-sm">
                            {newPatternNumber}
                          </div>
                        )}
                        <Button
                          type="button"
                          variant="outline"
                          size="icon"
                          onClick={() => setEditingPatternNo(!editingPatternNo)}
                        >
                          <Pencil size={16} />
                        </Button>
                      </div>

                      {garmentType === 'jacket' && (
                        <div className="space-y-2" data-testid="revision-belly-profile-wrap">
                          <Label className="text-xs text-muted-foreground" htmlFor="revision-belly-profile-select-trigger">
                            Belly
                          </Label>
                          <Select value={revisionBellyProfile || 'normal'} onValueChange={setRevisionBellyProfile}>
                            <SelectTrigger id="revision-belly-profile-select-trigger" data-testid="revision-belly-profile-select-trigger">
                              <SelectValue placeholder="Select belly profile" />
                            </SelectTrigger>
                            <SelectContent>
                              {JACKET_BELLY_PROFILE_OPTIONS.map((option) => (
                                <SelectItem
                                  key={option.value}
                                  value={option.value}
                                  data-testid={`revision-belly-profile-option-${option.value}`}
                                >
                                  {option.label}
                                </SelectItem>
                              ))}
                            </SelectContent>
                          </Select>
                        </div>
                      )}

                      {garmentType === 'jacket' && (
                        <div className="space-y-2" data-testid="revision-posture-wrap">
                          <Label className="text-xs text-muted-foreground" htmlFor="revision-posture-select-trigger">
                            Posture
                          </Label>
                          <Select value={revisionPosture || 'normal'} onValueChange={setRevisionPosture}>
                            <SelectTrigger id="revision-posture-select-trigger" data-testid="revision-posture-select-trigger">
                              <SelectValue placeholder="Select posture" />
                            </SelectTrigger>
                            <SelectContent>
                              {JACKET_POSTURE_OPTIONS.map((option) => (
                                <SelectItem
                                  key={option.value}
                                  value={option.value}
                                  data-testid={`revision-posture-option-${option.value}`}
                                >
                                  {option.label}
                                </SelectItem>
                              ))}
                            </SelectContent>
                          </Select>
                        </div>
                      )}

                      <div className="space-y-2">
                        <Label className="text-xs text-muted-foreground" htmlFor="revision-geometry-version-select-trigger">
                          Geometry Version
                        </Label>
                        <Select
                          value={revisionGeometryVersion || resolveGeometryVersion(garmentType, pattern || {})}
                          onValueChange={setRevisionGeometryVersion}
                        >
                          <SelectTrigger id="revision-geometry-version-select-trigger" data-testid="revision-geometry-version-select-trigger">
                            <SelectValue placeholder="Select geometry version" />
                          </SelectTrigger>
                          <SelectContent>
                            {orderedGeometryVersions.map((version) => (
                              <SelectItem
                                key={version}
                                value={version}
                                data-testid={`revision-geometry-version-option-${version}`}
                              >
                                {version}
                              </SelectItem>
                            ))}
                          </SelectContent>
                        </Select>
                        <p className="text-[11px] text-muted-foreground" data-testid="revision-geometry-version-helper-text">
                          Defaults to source pattern geometry; you can switch before opening this revision.
                        </p>
                        <p className="text-[11px] text-muted-foreground" data-testid="revision-geometry-version-current-text">
                          Source geometry: {resolveGeometryVersion(garmentType, pattern || {})}
                        </p>
                      </div>

                    </div>
                  )}
                </div>
              </div>
            )}
          </RadioGroup>
        </div>
        
        <DialogFooter>
          <Button variant="outline" onClick={() => onOpenChange(false)}>
            Cancel
          </Button>
          <Button onClick={handleProceed} disabled={loading}>
            {loading ? 'Processing...' : 'Proceed'}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
